import { Button } from '@lastbrand/web-ui/button'
import { ApplePayIcon } from '@lastbrand/web-ui/icons'
import { cx } from '@lastbrand/web-ui-styled-system/css'
import React, { useEffect } from 'react'

import {
  applePayButtonBaseCss,
  applePayCollapsedCss,
  applePayExpandedCss,
  applePayIconCss,
  applePayWrapperCss,
} from './apple-pay-button-ds.styles'
import { useApplePayBraintree } from './use-apple-pay-braintree.hook'

import type { ApplePayButtonProps } from './apple-pay-button.types'

const ApplePayButtonDS = ({
  className,
  buttonCss,
  dataSelectorKey,
  productPrice,
  applePayConfig,
  onReady,
  enableAnimation,
  disableClick,
}: ApplePayButtonProps) => {
  const {
    getCheckoutDetails,
    onError,
    validateApplePayClick,
    updateShippingAddress,
    updateShippingOption,
    checkoutExpressUpdateDetail,
    updateExpressCompleteDetails,
    capture,
    skipEmail,
    disableApplePayExpress,
    getZeroAmountCheckoutUrl,
    showZeroAmountCheckoutModal,
  } = applePayConfig

  const { isApplePayClickInProgress, isSupported, handleApplePayClick } = useApplePayBraintree({
    onError,
    getCheckoutDetails,
    validateApplePayClick,
    updateShippingAddress,
    productPrice,
    updateShippingOption,
    checkoutExpressUpdateDetail,
    updateExpressCompleteDetails,
    capture,
    skipEmail,
    getZeroAmountCheckoutUrl,
    showZeroAmountCheckoutModal,
  })

  const isSupportsApplePay = isSupported && !disableApplePayExpress
  const [shouldAnimate, setShouldAnimate] = React.useState(false)

  useEffect(() => {
    if (!isSupportsApplePay || !enableAnimation) {
      return
    }
    const timer = requestAnimationFrame(() => {
      setShouldAnimate(true)
      onReady?.()
    })
    return () => {
      cancelAnimationFrame(timer)
    }
  }, [isSupportsApplePay, onReady, enableAnimation])

  if (!isSupported || disableApplePayExpress) {
    return null
  }

  return (
    <div
      className={cx(
        className,
        applePayWrapperCss,
        enableAnimation ? (shouldAnimate ? applePayExpandedCss : applePayCollapsedCss) : undefined,
      )}
    >
      <Button
        variant="secondary"
        size="lg"
        onClick={handleApplePayClick}
        data-selector-key={dataSelectorKey}
        loading={isApplePayClickInProgress}
        disabled={disableClick || (enableAnimation && !shouldAnimate)}
        css={{ ...applePayButtonBaseCss, ...buttonCss }}
      >
        <ApplePayIcon role="presentation" className={applePayIconCss} />
      </Button>
    </div>
  )
}

export default ApplePayButtonDS
